import React, { memo, useCallback } from "react";

import cc from "classcat";
import { Tooltip } from "@narmi/design_system";

import { useSidebarContainer } from "../sidebar.hooks";
import PlainWrapper from "../../../../components/PlainWrapper";
import { requestNavigation } from "../../../utils/navigationGuard";

import styles from "./MenuItem.module.scss";

interface MenuItemHeaderProps {
  title?: string;
  icon?: string;
  route?: string;
  isOpen?: boolean;
  isActive?: boolean;
  onUserToggle?: (isOpen: boolean) => void;
}

const handleKeyDown = (callback: () => void) => (e: React.KeyboardEvent) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    callback();
  }
};

const MenuItemHeader = memo(function MenuItemHeader({
  icon,
  title,
  isOpen,
  route,
  isActive,
  onUserToggle = (_?: boolean) => {},
}: MenuItemHeaderProps) {
  const { isCollapsed: isSidebarCollapsed } = useSidebarContainer();
  const navigate = useCallback(() => {
    if (route) {
      requestNavigation(() => window.location.assign(route));
    } else if (onUserToggle) {
      onUserToggle(!isOpen);
    }
  }, [onUserToggle, route, isOpen]);

  const containerClassName = cc([
    styles["menu-item-row-container"],
    !isOpen && !isActive && styles.hover,
    isOpen && styles["menu-item-row-container-expanded"],
    isActive && styles["menu-item-row-active"],
  ]);
  const iconContainerClassName = cc([
    styles["icon-title-container"],
    styles["border-radius-override"],
  ]);
  const iconClassName = cc([
    `narmi-icon-${icon}`,
    styles["section-header-icon-size"],
  ]);
  const chevronContainerClassName = cc([
    styles["chevron-container"],
    !isOpen && !isActive && !isSidebarCollapsed && styles.hover,
  ]);
  const chevronClassName = cc([
    "narmi-icon-chevron-down",
    styles.chevron,
    isOpen && styles["chevron--rotated"],
  ]);

  const Wrapper = isSidebarCollapsed ? Tooltip : PlainWrapper;

  return (
    <Wrapper text={title ?? ""} side="right" wrapperDisplay="block">
      <div className={containerClassName}>
        <div
          className={iconContainerClassName}
          role="button"
          tabIndex={0}
          onClick={navigate}
          onKeyDown={handleKeyDown(navigate)}
        >
          {icon && <div className={iconClassName} />}
          <span>{title ?? ""}</span>
        </div>

        {isOpen !== undefined && (
          <div
            className={chevronContainerClassName}
            onClick={() => {
              if (onUserToggle) {
                onUserToggle(isOpen);
              }
            }}
            role="button"
            aria-expanded={isOpen}
            tabIndex={!isSidebarCollapsed ? 0 : undefined}
            onKeyDown={handleKeyDown(() => onUserToggle(isOpen))}
          >
            <span className={chevronClassName} />
          </div>
        )}
      </div>
    </Wrapper>
  );
});

export default MenuItemHeader;
